/**
 * LocalStorage utility for managing acknowledged release notes as a fallback
 * when server acknowledgment fails. localStorage is the authoritative
 * "user has seen it" record; the backend ack is best-effort / eventually consistent.
 */

const STORAGE_KEY = 'oliasoft_forecast_acknowledged_release_notes';
const RETENTION_DAYS = 60;

interface AcknowledgedNote {
  id: string;
  acknowledgedAt: string;
}

/**
 * Get acknowledged notes with full timestamp data (internal helper)
 */
const getAcknowledgedNotesWithTimestamps = (): AcknowledgedNote[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Failed to read acknowledged release notes from localStorage:', error);
    return [];
  }
};

/**
 * Get all acknowledged release note IDs from localStorage
 */
export const getAcknowledgedReleaseNotes = (): string[] => {
  return getAcknowledgedNotesWithTimestamps().map((note) => note.id);
};

/**
 * Add multiple release note IDs to the acknowledged list in localStorage
 */
export const acknowledgeReleaseNotesLocally = (releaseIds: string[]): void => {
  try {
    const existing = getAcknowledgedNotesWithTimestamps();
    const timestamp = new Date().toISOString();

    const newNotes: AcknowledgedNote[] = releaseIds.map((id) => ({
      id,
      acknowledgedAt: timestamp,
    }));

    const filtered = existing.filter((note) => !releaseIds.includes(note.id));
    const updated = [...filtered, ...newNotes];

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.warn('Failed to acknowledge release notes in localStorage:', error);
  }
};

/**
 * Remove acknowledged notes older than the retention window
 */
export const cleanupOldAcknowledgedNotes = (): void => {
  try {
    const existing = getAcknowledgedNotesWithTimestamps();
    const retentionDate = new Date();
    retentionDate.setDate(retentionDate.getDate() - RETENTION_DAYS);

    const filtered = existing.filter((note) => {
      const acknowledgedDate = new Date(note.acknowledgedAt);
      return acknowledgedDate > retentionDate;
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.warn('Failed to cleanup old acknowledged release notes:', error);
  }
};

/**
 * Remove a single acknowledged note (called once the backend confirms the ack,
 * so localStorage only retains acks the backend hasn't confirmed yet)
 */
export const removeAcknowledgedReleaseNote = (releaseId: string): void => {
  try {
    const existing = getAcknowledgedNotesWithTimestamps();
    const updated = existing.filter((note) => note.id !== releaseId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.warn('Failed to remove note from localStorage:', error);
  }
};
